import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Loader2, Flame, Sparkles, Shuffle, Search, Award, X } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { GENRES, PLATFORMS, Chip } from "@/components/games/gameMeta";
import MemberModal from "@/components/community/MemberModal";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/public/AuthModal";
import PublishGameModal from "@/components/games/PublishGameModal";
import { PodiumCard, SmallCard, GameTile } from "@/components/games/GameCards";

function Heading({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4">
      <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2"><Icon size={20} className="text-[hsl(var(--primary))]" /> {title}</h2>
      {sub && <span className="text-xs text-white/40">{sub}</span>}
    </div>
  );
}

function MoreGames() {
  const [seed] = useState(() => Math.floor(Math.random() * 1e6));
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  const lock = useRef(false);
  const loadMore = useCallback(() => {
    if (lock.current || !hasMore) return;
    lock.current = true; setBusy(true);
    api.get("/gamehub/more", { params: { seed, skip: items.length, limit: 12 } })
      .then((r) => { setItems((cur) => { const seen = new Set(cur.map((g) => g.id)); return [...cur, ...r.data.items.filter((g) => !seen.has(g.id))]; }); setHasMore(r.data.has_more); })
      .catch(() => setHasMore(false)).finally(() => { lock.current = false; setBusy(false); });
  }, [hasMore, items.length, seed]);

  useEffect(() => {
    const ob = new IntersectionObserver((e) => e[0].isIntersecting && loadMore(), { rootMargin: "400px" });
    if (ref.current) ob.observe(ref.current);
    return () => ob.disconnect();
  }, [loadMore]);

  return (
    <section className="mt-14" data-testid="more-games">
      <Heading icon={Shuffle} title="More Games" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => <GameTile key={g.id} g={g} />)}
      </div>
      <div ref={ref} className="py-8 grid place-items-center text-white/30 text-sm">{busy ? <Loader2 className="animate-spin" /> : !hasMore && items.length > 0 ? "You've seen every game" : null}</div>
    </section>
  );
}

function TopReviewers() {
  const [items, setItems] = useState(null);
  const [member, setMember] = useState(null);
  const close = useCallback(() => setMember(null), []);
  useEffect(() => { api.get("/gamehub/reviewers").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return null;
  return (
    <section className="mt-14" data-testid="top-reviewers">
      <Heading icon={Award} title="Top Reviewers" sub="Rating × 2 + comment · last 30 days" />
      {items.length === 0 ? <p className="card-surface rounded-xl p-5 text-sm text-white/40" data-testid="top-reviewers-empty">No reviewers yet — rate and comment on games to earn the Top Reviewer badge.</p> : (
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
          {items.map((r) => (
            <button key={r.user_id} onClick={() => setMember(r.user_id)} data-testid={`top-reviewer-${r.rank}`} className="shrink-0 w-40 card-surface rounded-xl p-4 text-left hover-rise">
              <div className="flex items-center justify-between">
                {r.avatar ? <img src={mediaUrl(r.avatar)} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="w-10 h-10 rounded-full bg-white/10 grid place-items-center font-bold text-white/70">{r.name[0]}</span>}
                <span className={`font-head text-lg font-bold ${r.rank <= 3 ? "text-yellow-400" : "text-white/40"}`}>#{r.rank}</span>
              </div>
              <div className="mt-2 text-sm font-bold text-white truncate">{r.name}</div>
              <div className="text-[11px] text-white/40">{r.ratings} ratings · {r.comments} comments</div>
              <div className="mt-1 text-xs font-bold text-[hsl(var(--primary))]">{r.points} pts</div>
            </button>
          ))}
        </div>
      )}
      {member && <MemberModal userId={member} onClose={close} />}
    </section>
  );
}

function Filters({ q, setQ, genre, setGenre, platform, setPlatform }) {
  return (
    <div className="mt-6 space-y-3" data-testid="games-filters">
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search games or publishers…" data-testid="games-search"
          className="w-full pl-9 pr-9 py-2.5 rounded-full card-surface text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" />
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X size={14} /></button>}
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
        {PLATFORMS.map((p) => <Chip key={p} on={platform === p} onClick={() => setPlatform(platform === p ? "" : p)} testid={`filter-platform-${p}`}>{p}</Chip>)}
        <span className="w-px bg-white/10 mx-1 shrink-0" />
        {GENRES.map((g) => <span key={g} className="shrink-0"><Chip on={genre === g} onClick={() => setGenre(genre === g ? "" : g)} testid={`filter-genre-${g}`}>{g}</Chip></span>)}
      </div>
    </div>
  );
}

function Results({ q, genre, platform }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => api.get("/gamehub/search", { params: { q, genre, platform } }).then((r) => setRes(r.data)).catch(() => setRes({ items: [], total: 0 })), 250);
    return () => clearTimeout(t);
  }, [q, genre, platform]);
  return (
    <section className="mt-8" data-testid="games-results">
      <Heading icon={Search} title="Results" sub={res ? `${res.total} games` : ""} />
      {!res ? <Loader2 className="animate-spin text-white/30" /> : res.items.length === 0 ? <p className="text-sm text-white/40 py-10 text-center" data-testid="games-results-empty">No games match your filters.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{res.items.map((g) => <GameTile key={g.id} g={g} />)}</div>
      )}
    </section>
  );
}

export default function Games() {
  const { user, loading } = useAuth();
  const [hub, setHub] = useState(null);
  const [publish, setPublish] = useState(false);
  const [auth, setAuth] = useState(false);
  const [want, setWant] = useState(false);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [platform, setPlatform] = useState("");
  const filtering = q.trim() || genre || platform;
  useEffect(() => {
    if (!want || !user) return;
    setWant(false);
    setTimeout(() => setPublish(true), 250);
  }, [want, user]);

  useEffect(() => { api.get("/gamehub").then((r) => setHub(r.data)).catch(() => setHub({ top: [], new: [] })); }, []);

  const top3 = hub?.top.slice(0, 3) || [];
  const podium = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 pt-6 md:pt-8 pb-16" data-testid="games-page">
      <h1 className="sr-only">Games — community ratings, top popular and new releases</h1>
      <div className="flex justify-end">
        <button onClick={() => { if (loading) return; if (user) setPublish(true); else { setWant(true); setAuth(true); } }} data-testid="publish-game-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold hover:brightness-110 transition">
          <Plus size={16} /> Publish your game
        </button>
      </div>

      <Filters {...{ q, setQ, genre, setGenre, platform, setPlatform }} />

      {filtering ? <Results q={q.trim()} genre={genre} platform={platform} /> : !hub ? <div className="py-24 grid place-items-center text-white/30"><Loader2 className="animate-spin" /></div> : (
        <>
          <section className="mt-6" data-testid="top-popular-games">
            <Heading icon={Flame} title="Top Popular Game" sub="Ranked by community rating" />
            <div className="grid md:grid-cols-[1fr_1.15fr_1fr] md:items-end gap-4 md:gap-6">
              {podium.map((g) => <div key={g.id} className={g.rank === 1 ? "order-first md:order-none" : ""}><PodiumCard g={g} first={g.rank === 1} /></div>)}
            </div>
            {hub.top.length > 3 && (
              <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="top-games-4-10">
                {hub.top.slice(3, 10).map((g) => <SmallCard key={g.id} g={g} />)}
              </div>
            )}
          </section>

          {hub.new.length > 0 && (
            <section className="mt-14" data-testid="new-releases">
              <Heading icon={Sparkles} title="New Game Release" />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {hub.new.map((g) => <GameTile key={g.id} g={g} badge="New" />)}
              </div>
            </section>
          )}

          <TopReviewers />
          <MoreGames />
        </>
      )}
      <PublishGameModal open={publish} onClose={() => setPublish(false)} />
      <AuthModal open={auth} onClose={() => setAuth(false)} />
    </div>
  );
}
